import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import SideMenu from "../../Components/SideMenu";
import { createBroadcast, deleteBroadcast, getBroadcasts, resendBroadcast, updateBroadcast, uploadFileInChat } from "../../reducers/commonReducer";
import moment from "moment";

const BroadcastForm = ({
  titleVal, onTitleChange,
  contentVal, onContentChange,
  newFiles, onFileChange, onRemoveNew,
  existingFiles, onRemoveExisting,
}) => (
  <>
    <Row className="mb-3 mt-2">
      <Col md={6} sm={6}>
        <Form.Group>
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            placeholder="Title"
            value={titleVal}
            onChange={onTitleChange}
          />
        </Form.Group>
      </Col>
      <Col md={6} sm={6}>
        <Form.Group>
          <Form.Label>Reply Format</Form.Label>
          <Form.Control type="text" readOnly value="Text with optional images" />
        </Form.Group>
      </Col>
    </Row>
    <Row className="mb-3">
      <Col md={12}>
        <Form.Group>
          <Form.Label>Content</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Content"
            value={contentVal}
            onChange={onContentChange}
          />
        </Form.Group>
      </Col>
    </Row>
    <Row className="mb-3">
      <Col md={12}>
        <Form.Group>
          <Form.Label>Images</Form.Label>
          <Form.Control type="file" multiple accept="image/*" onChange={onFileChange} />
          <Form.Text className="text-muted">
            You can save one message with text and multiple images together.
          </Form.Text>
        </Form.Group>
      </Col>
    </Row>
    {(existingFiles.length > 0 || newFiles.length > 0) && (
      <Row className="mb-3">
        <Col md={12}>
          <div className="quick-reply-files-grid">
            {existingFiles.map((file) => {
              const fileId = file?.id || file?._id;
              return (
                <div className="quick-reply-file-card" key={fileId}>
                  <img src={file?.cloudUrl || file?.url} alt={file?.originalName || "file"} />
                  <button type="button" className="quick-reply-file-remove" onClick={() => onRemoveExisting(fileId)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              );
            })}
            {newFiles.map((file) => (
              <div className="quick-reply-file-card" key={`${file.name}-${file.lastModified}`}>
                <img src={URL.createObjectURL(file)} alt={file.name} />
                <button type="button" className="quick-reply-file-remove" onClick={() => onRemoveNew(file)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    )}
  </>
);

const Broadcast = () => {
  const dispatch = useDispatch();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [editExistingFiles, setEditExistingFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [resendingId, setResendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const fetchBroadcasts = async () => {
    setLoading(true);
    const resp = await dispatch(getBroadcasts());
    setBroadcasts(resp?.payload?.data || []);
    setLoading(false);
  };

  useEffect(() => { fetchBroadcasts(); }, []);

  const openEdit = (b) => {
    setEditTarget(b);
    setEditTitle(b.title || "");
    setEditContent(b.content || "");
    setEditExistingFiles(b.files || []);
    setEditSelectedFiles([]);
  };

  const handleFileChange = (e) => {
    const next = Array.from(e.target.files || []);
    if (next.length) setSelectedFiles((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const handleEditFileChange = (e) => {
    const next = Array.from(e.target.files || []);
    if (next.length) setEditSelectedFiles((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const uploadAndGetIds = async (newFiles, existingIds = []) => {
    let attachmentIds = [...existingIds];
    if (newFiles.length > 0) {
      const resp = await dispatch(uploadFileInChat({ fileupload: newFiles }));
      if (resp?.payload?.status) {
        const ids = resp.payload.uploads?.map((u) => u?.id).filter(Boolean) || [];
        attachmentIds = [...attachmentIds, ...ids];
      }
    }
    return attachmentIds;
  };

  const handleCreate = async () => {
    if (!content.trim() && selectedFiles.length === 0) return;
    setSending(true);
    const attachments = await uploadAndGetIds(selectedFiles);
    const resp = await dispatch(createBroadcast({ title, content, attachments }));
    setSending(false);
    if (resp?.payload?.status) {
      setShowCreate(false);
      setTitle("");
      setContent("");
      setSelectedFiles([]);
      fetchBroadcasts();
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim() && editExistingFiles.length === 0 && editSelectedFiles.length === 0) return;
    setSaving(true);
    const existingIds = editExistingFiles.map((f) => f?.id || f?._id).filter(Boolean);
    const attachments = await uploadAndGetIds(editSelectedFiles, existingIds);
    const resp = await dispatch(updateBroadcast({ id: editTarget.id || editTarget._id, title: editTitle, content: editContent, attachments }));
    setSaving(false);
    if (resp?.payload?.status) {
      setEditTarget(null);
      fetchBroadcasts();
    }
  };

  const handleResend = async (id) => {
    setResendingId(id);
    await dispatch(resendBroadcast(id));
    setResendingId(null);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    const resp = await dispatch(deleteBroadcast(id));
    setDeletingId(null);
    setConfirmDeleteId(null);
    if (resp?.payload?.status) fetchBroadcasts();
  };

  return (
    <>
      <SideMenu />
      <div className="mt-extra content container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Broadcasts</h5>
          <Button variant="warning" onClick={() => setShowCreate(true)}>
            + New Broadcast
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="grow" /></div>
        ) : broadcasts.length === 0 ? (
          <p className="text-muted text-center mt-5">No broadcasts yet. Create one to message all your customers.</p>
        ) : (
          <table className="table w-100">
            <thead className="normalplace">
              <tr>
                <th>Sr</th>
                <th>Title</th>
                <th>Message</th>
                <th>Sent To</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((b, index) => (
                <tr key={b.id || b._id}>
                  <td>{index + 1}</td>
                  <td>{b.title || <span className="text-muted">—</span>}</td>
                  <td style={{ maxWidth: 300 }}>
                    <div>{b.content}</div>
                    {b.files?.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {b.files.map((f) => (
                          <img
                            key={f?.id || f?._id}
                            src={f?.cloudUrl || f?.url}
                            alt=""
                            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                          />
                        ))}
                      </div>
                    )}
                  </td>
                  <td><span className="badge bg-success">{b.recipientCount} customers</span></td>
                  <td>{moment(b.createdAt).format("DD/MM/YY hh:mm A")}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => openEdit(b)}>
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                      <Button
                        variant="warning"
                        size="sm"
                        disabled={resendingId === (b.id || b._id)}
                        onClick={() => handleResend(b.id || b._id)}
                      >
                        {resendingId === (b.id || b._id)
                          ? <Spinner animation="border" size="sm" />
                          : <><i className="bi bi-send me-1"></i>Resend</>}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={deletingId === (b.id || b._id)}
                        onClick={() => setConfirmDeleteId(b.id || b._id)}
                      >
                        {deletingId === (b.id || b._id)
                          ? <Spinner animation="border" size="sm" />
                          : <i className="bi bi-trash"></i>}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      <Modal show={showCreate} onHide={() => !sending && setShowCreate(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Broadcast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BroadcastForm
            titleVal={title}
            onTitleChange={(e) => setTitle(e.target.value)}
            contentVal={content}
            onContentChange={(e) => setContent(e.target.value)}
            newFiles={selectedFiles}
            onFileChange={handleFileChange}
            onRemoveNew={(f) => setSelectedFiles((prev) => prev.filter((x) => x !== f))}
            existingFiles={[]}
            onRemoveExisting={() => {}}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleCreate} disabled={sending || (!content.trim() && selectedFiles.length === 0)}>
            {sending ? "Sending..." : "Submit"}
          </Button>
          <Button variant="danger" onClick={() => setShowCreate(false)} disabled={sending}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal show={!!confirmDeleteId} onHide={() => !deletingId && setConfirmDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Broadcast</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this broadcast? This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => handleDelete(confirmDeleteId)} disabled={!!deletingId}>
            {deletingId ? <Spinner animation="border" size="sm" /> : "Yes, Delete"}
          </Button>
          <Button variant="danger" onClick={() => setConfirmDeleteId(null)} disabled={!!deletingId}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit modal */}
      <Modal show={!!editTarget} onHide={() => !saving && setEditTarget(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Broadcast</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BroadcastForm
            titleVal={editTitle}
            onTitleChange={(e) => setEditTitle(e.target.value)}
            contentVal={editContent}
            onContentChange={(e) => setEditContent(e.target.value)}
            newFiles={editSelectedFiles}
            onFileChange={handleEditFileChange}
            onRemoveNew={(f) => setEditSelectedFiles((prev) => prev.filter((x) => x !== f))}
            existingFiles={editExistingFiles}
            onRemoveExisting={(id) => setEditExistingFiles((prev) => prev.filter((f) => (f?.id || f?._id) !== id))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleEdit} disabled={saving || (!editContent.trim() && editExistingFiles.length === 0 && editSelectedFiles.length === 0)}>
            {saving ? "Saving..." : "Submit"}
          </Button>
          <Button variant="danger" onClick={() => setEditTarget(null)} disabled={saving}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Broadcast;
